import type { HcpRegistry } from "../../service/prisma/client.ts";

/**
 * What we know about one national register of health professionals.
 *
 * In `shared/` because THREE places must agree on one register's shape: the API
 * boundary that refuses a malformed number, the PG CHECK
 * `hcp_registry_ids_registry_shape` that refuses to store one, and the webapp
 * modal that used to carry its own hard-coded `value.length === 11` — a French
 * assumption the API did not share, in a screen that never asks which country
 * the practitioner works in.
 *
 * Keyed by the `HcpRegistry` enum through `satisfies Record<HcpRegistry, …>`,
 * which is the point: adding a value to the enum in `schema.prisma` fails the
 * build until its definition lands here. A database table could not do that,
 * and the `pattern` is boundary validation that Zod needs synchronously in the
 * route — a table would mean a join before every check.
 */
type RegistryDefinition = {
  /** The register's name, as shown to a user and to a client. */
  label: string;
  /** ISO 3166-1 alpha-2 code of the country that keeps the register. */
  country: string;
  /**
   * The number's shape. The expression is identical to the PG CHECK
   * `hcp_registry_ids_registry_shape`, so the column and the boundary agree by
   * construction rather than by review. `hcp-registry-ids.integration.ts`
   * asserts the two copies still match.
   *
   * Shape only, never a checksum: the RPPS key carries a Luhn-style control
   * digit, and rejecting a mistyped-but-well-formed number at the boundary
   * would refuse a real practitioner over a transcription slip. The directory
   * lookup is what decides whether the number exists.
   */
  pattern: RegExp;
};

/** Supported national registries and their identifier formats. */
export const HCP_REGISTRIES = {
  stps: {
    label: "Autorisations-ID",
    country: "DK",
    pattern: /^[0-9BCDFGHJKLMNPQRSTVWXYZ]{5}$/,
  },
  big: { label: "BIG", country: "NL", pattern: /^[0-9]{11}$/ },
  inami: { label: "INAMI", country: "BE", pattern: /^[0-9]{11}$/ },
  npi: { label: "NPI", country: "US", pattern: /^[0-9]{10}$/ },
  om: { label: "Ordem dos Médicos", country: "PT", pattern: /^[0-9]{1,30}$/ },
  rpps: {
    // "Répertoire Partagé des Professionnels de Santé", kept by the Agence du
    // Numérique en Santé. Mirrored weekly into `rpps_professionals` from the
    // data.gouv LibreAccès extract (`service/rpps/import.ts`).
    label: "RPPS",
    country: "FR",
    pattern: /^[0-9]{11}$/,
  },
} as const satisfies Record<HcpRegistry, RegistryDefinition>;

/**
 * The same definitions, for lookup by a register name that is not yet typed —
 * a raw database row, a URL parameter, a CSV cell. `HCP_REGISTRIES` has literal
 * keys, so indexing it with a runtime string would need an `as`; `Map.get` is
 * natively typed `string → … | undefined`. Same reason as
 * `domain/timezone/country-timezone.ts`.
 */
export const HCP_REGISTRY_BY_NAME: ReadonlyMap<string, RegistryDefinition> =
  new Map(Object.entries(HCP_REGISTRIES));

// BIG's SOAP Initials filter accepts at least one ASCII letter and dots.
export const BIG_INITIALS_PATTERN = /^[A-Za-z.]*[A-Za-z][A-Za-z.]*$/;
export const BIG_INITIALS_MAX_LENGTH = 30;

/**
 * `true` when the identifier has the shape its register issues. Lives next to
 * the pattern it applies, so the API boundary, the CSV importer and the webapp
 * modal all ask the same question rather than each reaching for `.pattern`.
 */
export function isWellFormedRegistryIdentifier(params: {
  registry: HcpRegistry;
  identifier: string;
}): boolean {
  return HCP_REGISTRIES[params.registry].pattern.test(params.identifier);
}

export function registryForCountry(country: string): HcpRegistry | null {
  if (country === HCP_REGISTRIES.stps.country) return "stps";
  if (country === HCP_REGISTRIES.big.country) return "big";
  if (country === HCP_REGISTRIES.rpps.country) return "rpps";
  if (country === HCP_REGISTRIES.npi.country) return "npi";
  if (country === HCP_REGISTRIES.inami.country) return "inami";
  if (country === HCP_REGISTRIES.om.country) return "om";
  return null;
}

/** Canonical BIG storage follows KNMG guidance: nine or ten digits gain leading zeros.
 * https://www.knmg.nl/ik-ben-arts/gaia/regelgeving-en-faq/faq/faq-artsen */
export function normalizeRegistryIdentifier<
  Registry extends HcpRegistry,
>(entry: {
  registry: Registry;
  identifier: string;
}): { registry: Registry; identifier: string } {
  return {
    registry: entry.registry,
    identifier:
      entry.registry === "stps"
        ? entry.identifier.trim().toUpperCase()
        : entry.registry === "big" && /^[0-9]{9,10}$/.test(entry.identifier)
          ? entry.identifier.padStart(11, "0")
          : entry.identifier,
  };
}
